import React from 'react'
import styled from 'styled-components'
import { addDays, format } from 'date-fns'
import { DSButton, DSSpacer, DSText } from '@zoe/ds-web'
import { onNextClickType } from 'components/types'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { QuizBase, Sex } from '../../redux/quiz/types'
import { GoalCopy, resolveGoalTokens } from '../../libs/goals/goalCopy'

// e11 "plan progress" promise screen for the non-weight-loss cohort
// (Figma: plan_progress_no_weight loss). A "Your health" graph rising from today
// to today + planDays; the yellow dot travels up the line like the dot on the
// weight-loss and symptom-score graphs.
// https://www.figma.com/design/SHNQFk5bsHc6os6mZLhpTu/Web-quiz--app-?node-id=15334-198574

export type PlanProgressScreenProps = {
  // Supports {number}, {gender} (from sexTokens) and any goalTokens.
  title: string
  description: string
  graphTitle: string
  sexTokens: Record<string, Record<Sex, string>>
  goalTokens: Record<string, GoalCopy>
  planDays: number
}

type Props = PlanProgressScreenProps & {
  onNextClick: onNextClickType
  quiz: QuizBase
}

const GraphCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 16px 20px;
`

const SVG = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
`

// Must match the @font-face family in src/styles/fonts.css: a display name with
// spaces only resolves for people who have the font installed locally.
const FONT = "'LotaGrotesqueAlt1', sans-serif"
const TEXT = '#4B4F60'

// Geometry from the Figma "non-weight loss graph" (295 × 184 inner area).
const WIDTH = 295
const HEIGHT = 184
const GRID_LEFT = 10
const GRID_RIGHT = 285
const GRID_LINES = [34, 74, 114, 154]
const LINE_TOP = 33
const LINE_BOTTOM = 156

// Normalised vertices of the Figma line (x: 0→1 left to right, y: 0 = bottom, 1 = top),
// measured from "Vector 3972" in the non-weight loss graph component.
const HEALTH_LINE: [number, number][] = [
  [0, 0],
  [0.191, 0.152],
  [0.401, 0.402],
  [0.621, 0.561],
  [0.783, 0.795],
  [0.906, 0.874],
  [1, 1],
]

// The line draws up first so the dot does not jump the moment the screen opens.
const LINE_DRAW_MS = 700
const DOT_TRAVEL_MS = 1400 // quicker than ProjectionGraph (2400) — this graph has no end bubble to reveal

const HealthGraph: React.FC<{ title: string; endDateLabel: string }> = ({ title, endDateLabel }) => {
  const span = GRID_RIGHT - GRID_LEFT
  const d = HEALTH_LINE.map(
    ([t, f], i) => `${i === 0 ? 'M' : 'L'} ${GRID_LEFT + span * t} ${LINE_BOTTOM - (LINE_BOTTOM - LINE_TOP) * f}`,
  ).join(' ')

  return (
    <GraphCard>
      <SVG viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={title}>
        <text x={WIDTH / 2} y={14} textAnchor="middle" fontFamily={FONT} fontSize="14" fontWeight="600" fill={TEXT}>
          {title}
        </text>
        {GRID_LINES.map((y) => (
          <line key={y} x1={GRID_LEFT} x2={GRID_RIGHT} y1={y} y2={y} stroke="#E2E2E2" strokeWidth={1} />
        ))}
        <path
          d={d}
          fill="none"
          stroke="#2D3246"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray="1"
          strokeDashoffset="1"
        >
          <animate attributeName="stroke-dashoffset" from="1" to="0" dur={`${LINE_DRAW_MS}ms`} fill="freeze" />
        </path>
        {/* key={d} remounts the dot so the motion replays if the path changes. */}
        {/* Holds at the start while the line draws, then climbs it. */}
        <circle key={d} r={8} fill="#FFD100">
          <animateMotion
            path={d}
            dur={`${LINE_DRAW_MS + DOT_TRAVEL_MS}ms`}
            keyPoints="0;0;1"
            keyTimes={`0;${(LINE_DRAW_MS / (LINE_DRAW_MS + DOT_TRAVEL_MS)).toFixed(4)};1`}
            calcMode="linear"
            fill="freeze"
          />
        </circle>
        <text x={GRID_LEFT + 2} y={181} fontFamily={FONT} fontSize="14" fill={TEXT}>
          Today
        </text>
        <text x={GRID_RIGHT} y={181} textAnchor="end" fontFamily={FONT} fontSize="14" fill={TEXT}>
          {endDateLabel}
        </text>
      </SVG>
    </GraphCard>
  )
}

export const PlanProgressScreen: React.FC<Props> = ({
  title,
  description,
  graphTitle,
  sexTokens,
  goalTokens,
  planDays,
  onNextClick,
  quiz,
}) => {
  const sex: Sex = quiz.biological_sex ?? 'female'
  const tokens = {
    ...Object.fromEntries(Object.entries(sexTokens).map(([name, bySex]) => [name, bySex[sex]])),
    ...resolveGoalTokens(quiz.goals, goalTokens),
  }
  const endDateLabel = React.useMemo(() => format(addDays(new Date(), planDays), 'd MMM'), [planDays])

  return (
    <>
      <ScreenContainer>
        <DSText variant="fluid-heading-700" weight="semiBold">
          <ContextAwareText text={title} quiz={quiz} additionalTokens={tokens} />
        </DSText>
        <DSSpacer size={24} direction="vertical" />
        <HealthGraph title={graphTitle} endDateLabel={endDateLabel} />
        {/* Pinned under the graph, as in the design, not by the button. */}
        <DSSpacer size={24} direction="vertical" />
        <DSText variant="fluid-paragraph-300" color="secondary">
          <ContextAwareText text={description} quiz={quiz} additionalTokens={tokens} />
        </DSText>
      </ScreenContainer>
      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={() => (onNextClick as () => void)()}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              Continue
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </>
  )
}
